import { useState } from "react";
import { ChevronDown, CircleHelp } from "lucide-react";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { P0StepBadge } from "./P0StepBadge";
import {
    getP0StepStatus,
    type P0WorkflowState,
    type P0WorkflowStep,
} from "./p0Workflow";

const STEPS: P0WorkflowStep[] = [1, 2, 3, 4, 5, 6, 7];

const STEP_KEYS: Record<
    P0WorkflowStep,
    "p0Step1Help" | "p0Step2Help" | "p0Step3Help" | "p0Step4Help" | "p0Step5Help" | "p0Step6Help" | "p0Step7Help"
> = {
    1: "p0Step1Help",
    2: "p0Step2Help",
    3: "p0Step3Help",
    4: "p0Step4Help",
    5: "p0Step5Help",
    6: "p0Step6Help",
    7: "p0Step7Help",
};

interface Props {
    workflow: P0WorkflowState;
}

export function P0WorkflowHelp({ workflow }: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);

    return (
        <div className="rounded-lg border border-blue-100 bg-blue-50/50">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
            >
                <CircleHelp className="h-4 w-4 shrink-0 text-blue-700" />
                <span className="flex-1 text-xs font-medium text-blue-900">
                    {t("laura", "p0WorkflowTitle")}
                </span>
                <ChevronDown
                    className={`h-4 w-4 text-blue-700 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            <div
                className={`flex flex-wrap items-center gap-1 px-3 pb-2 ${open ? "" : "pb-2"}`}
                aria-hidden={!open}
            >
                {STEPS.map((step, i) => {
                    const status = getP0StepStatus(step, workflow);
                    return (
                        <span key={step} className="flex items-center gap-1">
                            <P0StepBadge
                                step={step}
                                status={status}
                                size="sm"
                                optional={step === 2}
                            />
                            {i < STEPS.length - 1 && (
                                <span className="text-gray-300 text-[10px]">›</span>
                            )}
                        </span>
                    );
                })}
            </div>
            {open && (
                <ol className="space-y-1.5 border-t border-blue-100 px-3 py-2 text-xs text-gray-700">
                    {STEPS.map((step) => (
                        <li key={step} className="flex gap-2">
                            <P0StepBadge
                                step={step}
                                status={getP0StepStatus(step, workflow)}
                                size="sm"
                                optional={step === 2}
                            />
                            <span>{t("laura", STEP_KEYS[step])}</span>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}
